import type { ReactNode } from 'react'
import { useMeSessionQuery } from '@/features/auth/hooks/use-me-session-query'
import { WorkspaceDeskBlockedPage } from '@/features/auth/pages/workspace-desk-blocked-page'
import type { WorkspaceDesk } from '@/config/workspace-desk'
import { resolveWorkspaceDeskFromHost } from '@/config/workspace-desk'
import { isDeskHost } from '@/config/workspace-desk-host'
import {
  isStaffDeskAllowed,
  resolveStaffHomeDesk,
} from '@/shared/lib/staff-capabilities'
import { AuthLoadingScreen } from '@/features/auth/components/require-auth'

export type WorkspaceDeskBlockedState = {
  currentDesk: WorkspaceDesk
  homeDesk: WorkspaceDesk
}

export function useWorkspaceDeskBlockedState(): WorkspaceDeskBlockedState | null {
  const session = useMeSessionQuery()
  const host = window.location.hostname

  if (!isDeskHost(host) || session.isLoading || session.isPlaceholderData) {
    return null
  }

  const desk = resolveWorkspaceDeskFromHost(host)
  const capabilities = session.data?.capabilities ?? []
  const roles = session.data?.roles ?? []

  if (!desk) {
    return null
  }

  if (!session.isLoading && session.data && capabilities.length === 0) {
    if (isStaffDeskAllowed(desk, capabilities, roles)) {
      return null
    }
    return {
      currentDesk: desk,
      homeDesk: resolveStaffHomeDesk(capabilities, roles),
    }
  }

  if (capabilities.length === 0) {
    return null
  }

  if (isStaffDeskAllowed(desk, capabilities, roles)) {
    return null
  }

  return {
    currentDesk: desk,
    homeDesk: resolveStaffHomeDesk(capabilities, roles),
  }
}

/**
 * Blocks signed-in users on the wrong desk subdomain (UX only — API enforces authorization).
 * Do not auto location.replace here: each desk host has its own localStorage session, and
 * bouncing (e.g. leftover PRO session vs HR login) caused continuous pro↔hr reloads for Anas.
 * Login still handoff-redirects once; this page offers a manual link that carries the session.
 */
export function WorkspaceDeskSessionGuard({ children }: { children: ReactNode }) {
  const blocked = useWorkspaceDeskBlockedState()
  const session = useMeSessionQuery()
  const capabilities = session.data?.capabilities ?? []
  const roles = session.data?.roles ?? []

  if (
    (session.isLoading || session.isPlaceholderData) &&
    isDeskHost(window.location.hostname)
  ) {
    return <AuthLoadingScreen />
  }

  if (blocked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-workspace">
        <WorkspaceDeskBlockedPage
          currentDesk={blocked.currentDesk}
          homeDesk={blocked.homeDesk}
          capabilities={capabilities}
          roles={roles}
        />
      </div>
    )
  }

  return children
}
